import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'
import { IdentifiableObject } from '../models'
import { CustomStatus } from '../models/Config'

// Types based on Django models

export interface UnitInspectionTranscript extends IdentifiableObject {
    transcript: string
    video: number | null
    start: number | null
    end: number | null
    unit_inspection: number | null
    inventory_config: number | null
    status: CustomStatus | null
}

export interface UnitInspectionVideo extends IdentifiableObject {
    unit_inspection: number
    video: string
    duration_seconds: number | null
    uploaded: boolean
    area_inspection: number | null
    transcripts?: UnitInspectionTranscript[]
    modified_date: string
}

export interface UpdateStatusResult {
    success: boolean
    error?: unknown
    data?: {
        id: number
        status: {
            id: number
            causes_flag: boolean
        }
        handled: boolean
        area_inspection: number
    }
}

export type LoriTaskStatus =
    | 'IDLE'
    | 'PENDING'
    | 'STARTED'
    | 'SUCCESS'
    | 'FAILED'

export interface AnalyzeWithLoriResponse {
    task_id: string
}

export interface LoriTaskStatusResponse {
    status: Exclude<LoriTaskStatus, 'IDLE'>
}

export const useVideoInspection = (
    unitInspectionId: number,
    enabled: boolean,
) => {
    const [isUpdating, setIsUpdating] = useState(false)
    const [loriTaskId, setLoriTaskId] = useState<string | null>(null)
    const [loriTaskStatus, setLoriTaskStatus] = useState<LoriTaskStatus>('IDLE')

    const { data: videosResponse, isLoading, error, refetch } = useQuery<
        AxiosResponse<UnitInspectionVideo[]>,
        Error
    >({
        queryKey: ['unitInspectionVideos', unitInspectionId],
        queryFn: () =>
            axiosInstance.get('/inspection/video-inspection/', {
                params: {
                    unit_inspection_id: unitInspectionId,
                },
            }),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: enabled && unitInspectionId > 0,
    })

    const videos = videosResponse?.data ?? []

    const {
        data: loriTaskStatusResponse,
        isFetching: isPollingLoriTask,
    } = useQuery<AxiosResponse<LoriTaskStatusResponse>, Error>({
        queryKey: ['loriAnalysisStatus', unitInspectionId, loriTaskId],
        queryFn: () =>
            axiosInstance.post(
                '/inspection/unit-inspection/analyze_with_lori_status/',
                {
                    unit_inspection_id: unitInspectionId,
                    task_id: loriTaskId,
                },
            ),
        enabled: enabled && unitInspectionId > 0 && loriTaskId !== null,
        refetchOnWindowFocus: false,
        refetchInterval: (query) => {
            if (!query) return false
            const latestData = (query as any).state?.data as
                | AxiosResponse<LoriTaskStatusResponse>
                | undefined
            const status = latestData?.data?.status

            if (status === 'SUCCESS' || status === 'FAILED') {
                return false
            }
            return 3000
        },
    })

    useEffect(() => {
        const nextStatus = loriTaskStatusResponse?.data?.status
        if (!nextStatus) return

        setLoriTaskStatus(nextStatus)
        if (nextStatus === 'SUCCESS' || nextStatus === 'FAILED') {
            setLoriTaskId(null)
        }
    }, [loriTaskStatusResponse?.data?.status])

    useEffect(() => {
        setLoriTaskId(null)
        setLoriTaskStatus('IDLE')
    }, [unitInspectionId])

    // Update inventory inspection status
    const updateInventoryStatus = async (
        inventoryInspectionId: number,
        newStatusId: number,
        handled?: boolean,
    ): Promise<UpdateStatusResult> => {
        setIsUpdating(true)

        try {
            const change: { status: number; handled?: boolean } = {
                status: newStatusId,
            }

            if (handled !== undefined) {
                change.handled = handled
            }

            const response = await axiosInstance.patch(
                `inspection/inventory-inspection/${inventoryInspectionId}/`,
                change,
            )

            if (response.status === 200) {
                setIsUpdating(false)
                return {
                    success: true,
                    data: response.data,
                }
            }

            setIsUpdating(false)
            return { success: false }
        } catch (error) {
            console.error('Error updating inventory status:', error)
            setIsUpdating(false)
            return { success: false, error }
        }
    }

    // Mark an inspection as complete
    const completeInspection = async (
        unitInspectionId: number,
    ): Promise<{ success: boolean; error?: unknown }> => {
        setIsUpdating(true)

        try {
            const response = await axiosInstance.patch(
                `inspection/unit-inspection/${unitInspectionId}/`,
                {
                    complete: true,
                },
            )

            if (response.status === 200) {
                setIsUpdating(false)
                return { success: true }
            }

            setIsUpdating(false)
            return { success: false }
        } catch (error) {
            console.error('Error completing inspection:', error)
            setIsUpdating(false)
            return { success: false, error }
        }
    }

    // Upload a video for an area inspection
    const uploadVideo = async (
        video: File,
        areaInspectionId: number,
        unitInspectionId: number,
    ): Promise<{ success: boolean; data?: unknown; error?: unknown }> => {
        setIsUpdating(true)

        try {
            const formData = new FormData()
            formData.append('video', video)
            formData.append('areaId', areaInspectionId.toString())

            const response = await axiosInstance.post(
                `/inspection/unit-inspection/${unitInspectionId}/upload_video/`,
                formData,
            )

            if (response.status === 200) {
                refetch()
                setIsUpdating(false)
                return { success: true, data: response.data }
            }

            toast.error('Failed to upload video')
            setIsUpdating(false)
            return { success: false }
        } catch (error) {
            console.error('Error uploading video:', error)
            toast.error('Failed to upload video')
            setIsUpdating(false)
            return { success: false, error }
        }
    }

    // Analyze video with Lori AI
    const analyzeWithLori = async (): Promise<{
        success: boolean
        data?: AnalyzeWithLoriResponse
        error?: unknown
    }> => {
        setIsUpdating(true)

        try {
            const response = await axiosInstance.post<AnalyzeWithLoriResponse>(
                `/inspection/unit-inspection/analyze_with_lori/`,
                {
                    unit_inspection_id: unitInspectionId,
                },
            )

            const taskId = response.data?.task_id
            if (!taskId) {
                setIsUpdating(false)
                return {
                    success: false,
                    error: new Error('Lori analysis did not return a task_id'),
                }
            }

            setLoriTaskId(taskId)
            setLoriTaskStatus('PENDING')
            setIsUpdating(false)
            return { success: true, data: response.data }
        } catch (error) {
            console.error('Error analyzing with Lori:', error)
            setIsUpdating(false)
            return { success: false, error }
        }
    }

    const resetLoriTask = (): void => {
        setLoriTaskId(null)
        setLoriTaskStatus('IDLE')
    }

    return {
        videos,
        isLoading,
        error,
        refetch,
        isUpdating,
        updateInventoryStatus,
        completeInspection,
        uploadVideo,
        analyzeWithLori,
        loriTaskId,
        loriTaskStatus,
        isPollingLoriTask,
        resetLoriTask,
    }
}
